import { IDataSet, DataLoadCallback, DataLoadFailedCallback, DataLoadedCallback } from '@/interfaces/';
import { Query } from './Query';
import { Page } from "./Page";
import { Sort, SortDirections } from "./Sort";
import { Expand } from "./Expand";

/**
 * The DataSet model class
 */
export class DataSet<T> implements IDataSet<T> {
  /**
   * The fields to retrieve
   */
  public Fields: string[] = [];

  /**
   * The query
   */
  public Query: Query = new Query();

  /**
   * The paging information
   */
  public Page: Page = new Page();

  /**
   * The sort orders for fields
   */
  public SortOrders: Sort[] = [];

  /**
   * The expansions
   */
  public Expands: Expand[] = [];

  /**
   * The Records
   */
  public Records: T[] = [];

  /**
   * Indicates if the dataset is loading
   */
  public Loading: boolean = false;

  /**
   * The Filter Changed Timeout ID
   */
  private FilterChangedTimeoutID: number = 0;

  /**
   * Create a new DataSet
   * @param fields The fields
   * @param query The query
   * @param sortOrders The sort orders
   * @param expands The expansions
   * @param page The page
   */
  constructor(fields: string[] = [], query: Query = new Query(), sortOrders: Sort[] = [], expands: Expand[] = [], page: Page = new Page()) {
    this.Fields = fields;
    this.Query = query;
    this.SortOrders = sortOrders;
    this.Expands = expands;
    this.Page = page;
  }

  public DataLoadFailedCallback: DataLoadFailedCallback = () => {
    this.Loading = false;
  }

  public DataLoadedCallback: DataLoadedCallback<T> = () => {
    return;
  }

  public OnLoadCompleted: () => void = () => { };

  public Configure(dataLoadCallback: DataLoadCallback<T>) {
    let dataSet = this;

    dataSet.DataLoadCallback = dataLoadCallback;
    dataSet.DataLoadedCallback = (records, totalRecords) => {
      dataSet.Records = records;

      dataSet.Page.TotalRecords = totalRecords;
      dataSet.Page.TotalPages = dataSet.Page.TotalRecords > dataSet.Page.RecordsPerPage ? Math.ceil(dataSet.Page.TotalRecords / dataSet.Page.RecordsPerPage) : dataSet.Page.CurrentPage;

      dataSet.Loading = false;

      dataSet.OnLoadCompleted();
    };
  }

  public Load() {
    this.Loading = true;
    this.DataLoadCallback(this);
  }

  public SetPage(pageNumber: number) {
    this.Page.CurrentPage = pageNumber;
    this.Load();
  }

  public SetSort(context: any) {
    this.SortOrders = [new Sort(context.sortBy, context.sortDesc ? SortDirections.Descending : SortDirections.Ascending)];
    this.Load();
  }

  public ToDataQuery(): string {
    let query: string = "";

    query += `$count=true`;
    query += `&$skip=${this.Page.Skip}`;
    query += `&$top=${this.Page.RecordsPerPage}`;
    query += this.Query.ToDataQuery();
    if (this.SortOrders.length > 0) {
      let orders: string[] = [];
      for (let sortOrder of this.SortOrders) {
        orders.push(sortOrder.ToDataQuery());
      }
      query += `&$orderby=${orders.join(",")}`;
    }
    if (this.Expands.length > 0) {
      let expands: string[] = [];
      for (let expand of this.Expands) {
        expands.push(expand.ToDataQuery());
      }
      query += `&$expand=${expands.join(",")}`;
    }
    
    return query;
  }

  public OnFilterChanged(): void {
    let dataSet = this;
    if (this.FilterChangedTimeoutID !== 0) { clearTimeout(this.FilterChangedTimeoutID); }
    this.FilterChangedTimeoutID = setTimeout(function () {
      dataSet.Load();
    }, 500);
  }

  private DataLoadCallback: DataLoadCallback<T> = () => { return; };
}

